import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { Loader2, Shield, FileText, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { requestLocationPermission } from "../hooks/useSilentLocationTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TermsAcceptanceModal({ open, onAccepted, userName }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [terms, setTerms] = useState("");
  const [filialNome, setFilialNome] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [requestingLocation, setRequestingLocation] = useState(false);

  useEffect(() => {
    if (open) {
      loadTerms();
    }
  }, [open]);

  const loadTerms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/terms`);
      setTerms(response.data.termos_uso || "");
      setFilialNome(response.data.filial_nome || "");
    } catch (error) {
      console.error("Error loading terms:", error);
      toast.error("Erro ao carregar termos de uso");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!acceptTerms) {
      toast.error("Você precisa aceitar os termos de uso para continuar");
      return;
    }

    try {
      setSubmitting(true);
      setRequestingLocation(true);
      
      // Solicitar permissão de localização ao navegador (APENAS UMA VEZ)
      const locationGranted = await requestLocationPermission();
      
      setRequestingLocation(false);
      
      // Salvar aceite no backend
      await axios.post(`${API}/auth/accept-terms`, {
        permitir_localizacao: locationGranted
      });
      
      if (locationGranted) {
        toast.success("Termos aceitos e localização habilitada!");
      } else {
        toast.success("Termos aceitos! (Localização não habilitada)");
      }
      
      onAccepted && onAccepted();
    } catch (error) {
      console.error("Error accepting terms:", error);
      toast.error("Erro ao aceitar termos");
    } finally {
      setSubmitting(false);
      setRequestingLocation(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-lg w-[95vw] max-h-[85vh] flex flex-col p-4 sm:p-6" 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5 text-primary" />
            Termos de Uso
          </DialogTitle>
          <DialogDescription className="text-sm">
            {userName && <span>Olá, <strong>{userName}</strong>! </span>}
            Para continuar usando o aplicativo, você precisa aceitar os termos de uso
            {filialNome && <span> da <strong>{filialNome}</strong></span>}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="flex flex-col gap-3 flex-1 min-h-0">
            {/* Terms Content - Scrollable */}
            <div className="rounded-lg border bg-muted/30 flex-1 min-h-0 flex flex-col">
              <div className="flex items-center gap-2 p-2 border-b bg-muted/50">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium">Termos e Condições</span>
              </div>
              <ScrollArea className="flex-1 max-h-[30vh] p-3">
                <div className="text-xs whitespace-pre-wrap text-muted-foreground leading-relaxed">
                  {terms || "Termos de uso não configurados."}
                </div>
              </ScrollArea>
            </div>

            {/* Single Acceptance Checkbox */}
            <div className="p-3 rounded-lg border bg-card">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="accept-terms" className="text-sm font-medium cursor-pointer leading-tight">
                    Li e concordo com os termos de uso acima
                  </Label>
                  <p className="text-xs text-muted-foreground leading-tight">
                    Ao aceitar, você concorda com todas as condições descritas, incluindo autorização de imagem e uso de localização para check-in automático.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button - Fixed at bottom */}
            <Button 
              onClick={handleAccept} 
              className="w-full shrink-0" 
              size="default"
              disabled={!acceptTerms || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {requestingLocation ? "Aguardando..." : "Processando..."}
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Aceitar e Continuar
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
